"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMapGL, { AttributionControl, type MapRef, Marker, type ViewState } from "react-map-gl";
import "./mapbox-overrides.css";
import { getMapboxTokenFromEnv } from "../../config/mapbox";
import { useColorMode } from "../../utils/ColorModeProvider";
import { Dialog } from "../Dialog";
import { Icon } from "../Icon";
import { ControlButton } from "./controls/primitives/ControlButton";
import { InteractiveMap } from "./InteractiveMap";
import { type LayerSpec, renderLayers } from "./layers";
import { ensureMapboxCss } from "./loadMapboxCss";
import { MapLoadingOverlay } from "./MapLoadingOverlay";
import { MapPlaceholder } from "./MapPlaceholder";
import { useMapRenderOptions } from "./MapRenderProvider";
import { MapSnapshot } from "./MapSnapshot";
import { useMapStyleOverride } from "./MapStyleProvider";
import { getMapStyle, type MapType } from "./mapConfig";
import { prepareGLMap, useMapExportRegistration, waitForMapReady } from "./mapExport";

export interface StaticMapProps {
  /**
   * Map width
   * @default "100%"
   */
  width?: number | string;
  /**
   * Map height
   * @default "100%"
   */
  height?: number | string;
  /**
   * Initial viewport configuration
   */
  initialViewState?: Partial<ViewState>;
  /**
   * Show loading skeleton
   */
  isLoading?: boolean;
  /**
   * Map type - the visual style of the map
   * Automatically adapts to light/dark mode via ColorModeProvider
   *
   * - "streets": Street map with roads and labels
   * - "satellite": Satellite imagery with street overlays
   * - "neutral": Minimal/clean map design
   *
   * @default "streets"
   */
  mapType?: MapType;
  /** Per-organization Mapbox style URL; defaults to the MapStyleProvider value. */
  mapStyleUrl?: string | null;
  /**
   * Layers to render on the map
   *
   * Use the layers API for clean, type-safe layer configuration.
   * Same API as InteractiveMap for consistency.
   *
   * @example
   * ```tsx
   * import { layer } from '@texturehq/edges';
   *
   * <StaticMap
   *   layers={[
   *     layer.geojson({
   *       id: 'points',
   *       data: myGeoJson,
   *       renderAs: 'circle',
   *       style: { color: { token: 'brand-primary' } }
   *     })
   *   ]}
   * />
   * ```
   */
  layers?: LayerSpec[];
  /**
   * Mapbox access token
   * @default getMapboxTokenFromEnv()
   */
  mapboxAccessToken?: string;
  /**
   * Show marker at center
   * @default true
   */
  showMarker?: boolean;
  /**
   * Vertical pixel offset for the center marker. Positive moves it DOWN.
   *
   * The marker is anchored at its tip (`anchor="bottom"`), so the tip sits on
   * the exact center of the map and the whole glyph stands above it. That is
   * correct for a map you can read the geography of, but in a small thumbnail
   * with no surrounding context the pin reads as floating near the top edge.
   * Offsetting by roughly half the marker height moves the glyph's visual mass
   * onto the optical center.
   *
   * Presentational only — it shifts the marker, not the viewport, so the map
   * stays centered on the real coordinate. Use it on decorative thumbnails,
   * NOT where a reader is meant to trust the tip as the precise location.
   * @default 0
   */
  markerOffsetY?: number;
  /**
   * Show Mapbox attribution control
   * @default false
   */
  showAttribution?: boolean;
  /**
   * Callback fired when the map finishes loading
   * Useful for knowing when Mapbox is fully initialized and tiles are loaded
   */
  onLoad?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Show expand button to open interactive fullscreen map
   * When enabled, shows a button in the top-right corner that opens
   * the map in an interactive dialog
   * @default false
   */
  showExpandToggle?: boolean;
  /**
   * Title for the expanded map dialog
   * @default "Explore map"
   */
  expandedMapTitle?: string;
  /**
   * Optional address or location label to show in expanded view
   */
  expandedMapAddressLabel?: string;
  /**
   * When no Mapbox token is available, render the designed **schematic**
   * placeholder (grid + centered pin + soft radius circle) instead of the bare
   * "Map requires Mapbox token" message. Additive: default `false` keeps the
   * message so existing consumers are unchanged. A real token always shows the
   * real map regardless of this flag.
   * @default false
   */
  schematicPlaceholder?: boolean;
}

const MAP_LOADED_POLL_MS = 1000;

/**
 * StaticMap
 *
 * A non-interactive map component for displaying a location.
 * Perfect for showing addresses, store locations, or any single point of interest.
 *
 * Automatically adapts to light/dark mode using the global ColorModeProvider.
 */
export function StaticMap({
  width = "100%",
  height = "100%",
  initialViewState = {
    longitude: -122.4194,
    latitude: 37.7749,
    zoom: 14,
  },
  isLoading = false,
  mapType = "streets",
  mapStyleUrl,
  layers = [],
  mapboxAccessToken,
  showMarker = true,
  markerOffsetY = 0,
  showAttribution = false,
  onLoad,
  className,
  showExpandToggle = false,
  expandedMapTitle = "Explore map",
  expandedMapAddressLabel,
  schematicPlaceholder = false,
}: StaticMapProps) {
  // Lazy evaluation of token - only call getMapboxTokenFromEnv() if not provided
  const rendering = useMapRenderOptions();
  const token = mapboxAccessToken ?? rendering.mapboxAccessToken ?? getMapboxTokenFromEnv();
  const [capturing, setCapturing] = useState(false);
  const { isDarkTheme } = useColorMode();
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [showLoadingState, setShowLoadingState] = useState(false);
  const orgStyleOverride = useMapStyleOverride();
  const resolvedStyleOverride = mapStyleUrl ?? orgStyleOverride;
  const [isExpanded, setIsExpanded] = useState(false);
  const mapRef = useRef<MapRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapFailed = useRef(false);
  useEffect(() => {
    mapFailed.current = false;
  }, [token, mapType, isDarkTheme, resolvedStyleOverride]);
  const resolvedStyle = getMapStyle(mapType, isDarkTheme, resolvedStyleOverride);
  const supportsImage =
    !initialViewState.padding &&
    (initialViewState.pitch ?? 0) <= 60 &&
    layers.length === 0 &&
    /^mapbox:\/\/styles\/[\w-]+\/[\w-]+$/.test(resolvedStyle);
  const imageMode = supportsImage && (capturing || rendering.mode === "export");
  useMapExportRegistration(containerRef, async (signal) => {
    if (!token) throw new Error("Configure a public Mapbox token to export this map.");
    setCapturing(true);
    const release = () => setCapturing(false);
    try {
      if (!supportsImage) {
        const restore = await prepareGLMap(
          () => mapRef.current,
          signal,
          () => mapFailed.current
        );
        return () => {
          restore();
          release();
        };
      }
      await waitForMapReady(() => {
        if (!containerRef.current?.isConnected) throw new Error("Map was removed during export.");
        const snapshot = containerRef.current.querySelector<HTMLElement>("[data-map-snapshot]");
        if (snapshot?.dataset.mapState === "error") throw new Error(snapshot.dataset.mapError);
        return snapshot?.dataset.mapState === "ready";
      }, signal);
      return release;
    } catch (error) {
      release();
      throw error;
    }
  });

  // Handlers for expand dialog - memoized to avoid inline function warnings
  const handleExpandOpen = useCallback(() => setIsExpanded(true), []);
  const handleExpandClose = useCallback(() => setIsExpanded(false), []);

  useEffect(() => {
    void ensureMapboxCss();
  }, []);

  const containerStyle = {
    width: typeof width === "number" ? `${width}px` : width,
    height: typeof height === "number" ? `${height}px` : height,
  };

  // Delay showing loading state to avoid flash for fast loads
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isMapLoaded) {
        setShowLoadingState(true);
      }
    }, 200); // 200ms delay before showing loading state

    return () => clearTimeout(timer);
  }, [isMapLoaded]);

  const loadReported = useRef(false);
  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true);
    setShowLoadingState(false);
    if (loadReported.current) return;
    loadReported.current = true;
    onLoad?.();
  }, [onLoad]);

  // `load` fires once; a map remounted while hidden or mid-layout can miss it, so `idle` and `loaded()` also count.
  const settleIfLoaded = useCallback(() => {
    if (mapRef.current?.loaded?.()) handleMapLoad();
  }, [handleMapLoad]);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) {
        mapRef.current.resize();
        settleIfLoaded();
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [settleIfLoaded]);

  useEffect(() => {
    if (isMapLoaded) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") settleIfLoaded();
    };
    document.addEventListener("visibilitychange", onVisible);
    const failsafe = setInterval(settleIfLoaded, MAP_LOADED_POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(failsafe);
    };
  }, [isMapLoaded, settleIfLoaded]);

  // Determine if we should show loading overlay
  const showLoadingOverlay = isLoading || showLoadingState;

  if (!token && !imageMode) {
    return (
      <div ref={containerRef} style={containerStyle} className={className}>
        <MapPlaceholder
          style={{ width: "100%", height: "100%" }}
          schematic={schematicPlaceholder}
          showMarker={showMarker}
        />
      </div>
    );
  }

  return (
    <div ref={containerRef} style={containerStyle} className={`relative ${className || ""}`}>
      {imageMode ? (
        <MapSnapshot
          token={token}
          mapStyle={resolvedStyle}
          view={initialViewState}
          showMarker={showMarker && initialViewState.longitude !== undefined && initialViewState.latitude !== undefined}
          markerOffsetY={markerOffsetY}
          marker={<Icon name="MapPin" size={36} className="text-map-marker-default" weight="fill" />}
        />
      ) : (
        <ReactMapGL
          ref={mapRef}
          mapboxAccessToken={token}
          initialViewState={initialViewState}
          style={{ width: "100%", height: "100%" }}
          mapStyle={getMapStyle(mapType, isDarkTheme, resolvedStyleOverride)}
          onLoad={handleMapLoad}
          onIdle={handleMapLoad}
          onError={() => {
            mapFailed.current = true;
          }}
          scrollZoom={false}
          dragPan={false}
          dragRotate={false}
          doubleClickZoom={false}
          touchZoomRotate={false}
          interactive={false}
          attributionControl={false}
        >
          {/* Attribution in top-left corner */}
          {(showAttribution || capturing || rendering.mode === "export") && (
            <AttributionControl position="top-left" compact={false} />
          )}

          {/* Render custom layers from layers API */}
          {renderLayers(layers, mapType)}

          {/* Show marker at center if enabled */}
          {showMarker && initialViewState.longitude !== undefined && initialViewState.latitude !== undefined && (
            <Marker
              longitude={initialViewState.longitude}
              latitude={initialViewState.latitude}
              anchor="bottom"
              offset={[0, markerOffsetY]}
            >
              <Icon name="MapPin" size={36} className="text-map-marker-default" weight="fill" />
            </Marker>
          )}
        </ReactMapGL>
      )}

      {/* Expand toggle button */}
      {showExpandToggle && !imageMode && (
        <div className="absolute top-4 right-4 z-20">
          <ControlButton icon="ArrowsOutSimple" ariaLabel="Expand map to fullscreen" onPress={handleExpandOpen} />
        </div>
      )}

      {/* Expanded map dialog - controlled mode for close icon support on mobile */}
      {showExpandToggle && !imageMode && (
        <Dialog
          isOpen={isExpanded}
          onClose={handleExpandClose}
          title={expandedMapTitle}
          contentPadding={false}
          maxWidth="min(1100px, 100vw)"
          minWidth="min(360px, 100vw)"
          height="80vh"
          mobileHeight="82vh"
        >
          <div className="flex flex-col h-full">
            <div className="flex-1 min-h-0 overflow-hidden sm:mx-4 sm:mt-4 sm:rounded-xl sm:border sm:border-border-default">
              <InteractiveMap
                initialViewState={initialViewState}
                mapType={mapType}
                // Pass the RESOLVED override, not the raw prop: the expanded
                // map would otherwise re-resolve from context and show a
                // different basemap than the preview it expanded from.
                mapStyleUrl={resolvedStyleOverride}
                mapboxAccessToken={token}
                showMarker={showMarker}
                markerLocation={
                  initialViewState.longitude !== undefined && initialViewState.latitude !== undefined
                    ? {
                        latitude: initialViewState.latitude,
                        longitude: initialViewState.longitude,
                      }
                    : undefined
                }
                layers={layers}
              />
            </div>

            {expandedMapAddressLabel && (
              <p className="text-sm text-text-secondary px-4 py-3 flex-shrink-0">{expandedMapAddressLabel}</p>
            )}
          </div>
        </Dialog>
      )}

      {/* Loading overlay */}
      {showLoadingOverlay && !imageMode && <MapLoadingOverlay />}
    </div>
  );
}
